/**
 * Structured Table View Component
 *
 * Renders parsed JSON/YAML/TOML data as hierarchical nested tables.
 * Objects become key-value tables, arrays of objects become column tables,
 * and nested structures are rendered as collapsible sub-tables.
 */

import { ChevronRight } from 'lucide-react';
import { memo,useCallback, useMemo, useState } from 'react';
import { parse as parseTOML } from 'smol-toml';
import YAML from 'yaml';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ToolError } from '@/engine/errors/ToolError';
import { parseJsonLenient } from '@/lib/jsonParser';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface StructuredTableViewProps {
  data: string;
  maxDepth?: number;
  expandedByDefault?: boolean;
}

interface ValueCellProps {
  value: unknown;
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

// ─── Parsing ────────────────────────────────────────────────────────────────

function parseInput(input: string): unknown {
  try {
    return parseJsonLenient(input);
  } catch {
    // not JSON
  }
  try {
    const result = parseTOML(input);
    if (result && typeof result === 'object') return result;
  } catch {
    // not TOML
  }
  try {
    const result = YAML.parse(input);
    if (result !== undefined && result !== null) return result;
  } catch {
    // not YAML
  }
  throw ToolError.validation('Unable to parse input as JSON, YAML, or TOML');
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function isPrimitive(value: unknown): boolean {
  return value === null || value === undefined || typeof value !== 'object';
}

function isArrayOfObjects(value: unknown): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.some((item) => item !== null && typeof item === 'object' && !Array.isArray(item));
}

function isArrayOfPrimitives(value: unknown): value is (string | number | boolean | null)[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every((item) => isPrimitive(item));
}

/** Extract all unique keys from an array of objects, preserving insertion order. */
function extractColumns(arr: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const cols: string[] = [];
  for (const item of arr) {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      for (const key of Object.keys(item)) {
        if (!seen.has(key)) {
          seen.add(key);
          cols.push(key);
        }
      }
    }
  }
  return cols;
}

function formatPrimitive(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'number') return String(value);
  return String(value);
}

// ─── ValueCell (recursive renderer) ─────────────────────────────────────────

const ValueCell = memo(function ValueCell({
  value,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: ValueCellProps) {
  // Null / undefined
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">null</span>;
  }

  // Primitives
  if (isPrimitive(value)) {
    if (typeof value === 'boolean') {
      return (
        <span className={cn('font-mono text-xs', value ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
          {String(value)}
        </span>
      );
    }
    if (typeof value === 'number') {
      return <span className="font-mono text-xs text-blue-600 dark:text-blue-400">{String(value)}</span>;
    }
    return <span className="text-xs">{String(value)}</span>;
  }

  // Circular reference check
  if (typeof value === 'object') {
    if (visited.has(value as object)) {
      return <span className="text-muted-foreground italic text-xs">[Circular]</span>;
    }
  }

  // Depth exceeded → JSON code block
  if (depth >= maxDepth) {
    let text: string;
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
    return (
      <code className="text-xs font-mono bg-muted/30 px-1 py-0.5 rounded break-all">
        {text}
      </code>
    );
  }

  // Track this object as visited for circular reference detection
  visited.add(value as object);

  // Empty array
  if (Array.isArray(value) && value.length === 0) {
    return <span className="text-muted-foreground italic text-xs">[]</span>;
  }

  // Empty object
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0) {
    return <span className="text-muted-foreground italic text-xs">{'{}'}</span>;
  }

  // Array of primitives → comma-separated inline
  if (isArrayOfPrimitives(value)) {
    return (
      <span className="text-xs">
        {(value as (string | number | boolean | null)[]).map((v) => formatPrimitive(v)).join(', ')}
      </span>
    );
  }

  // Array of objects → collapsible array table
  if (Array.isArray(value) && isArrayOfObjects(value)) {
    return (
      <CollapsibleArrayTable
        data={value as Record<string, unknown>[]}
        depth={depth}
        maxDepth={maxDepth}
        expandedByDefault={expandedByDefault}
        visited={visited}
      />
    );
  }

  // Mixed array → collapsible list
  if (Array.isArray(value)) {
    return (
      <CollapsibleArrayTable
        data={value}
        depth={depth}
        maxDepth={maxDepth}
        expandedByDefault={expandedByDefault}
        visited={visited}
      />
    );
  }

  // Object → collapsible object table
  if (typeof value === 'object') {
    return (
      <CollapsibleObjectTable
        data={value as Record<string, unknown>}
        depth={depth}
        maxDepth={maxDepth}
        expandedByDefault={expandedByDefault}
        visited={visited}
      />
    );
  }

  return <span className="text-xs">{String(value)}</span>;
});

ValueCell.displayName = 'ValueCell';

// ─── Collapse Toggle ────────────────────────────────────────────────────────

function CollapseToggle({
  expanded,
  onClick,
  summary,
}: {
  expanded: boolean;
  onClick: () => void;
  summary: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-xs text-muted-foreground',
        'hover:text-foreground transition-colors cursor-pointer',
      )}
    >
      <ChevronRight
        className={cn(
          'h-3 w-3 transition-transform duration-150',
          expanded && 'rotate-90',
        )}
      />
      {!expanded && <span className="font-mono">{summary}</span>}
    </button>
  );
}

// ─── CollapsibleObjectTable ─────────────────────────────────────────────────

function CollapsibleObjectTable({
  data,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: {
  data: Record<string, unknown>;
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}) {
  const [expanded, setExpanded] = useState(expandedByDefault);
  const toggle = useCallback(() => setExpanded((v) => !v), []);
  const keys = useMemo(() => Object.keys(data), [data]);

  const summary = `{${keys.length}}`;

  return (
    <div className="inline-block">
      <CollapseToggle expanded={expanded} onClick={toggle} summary={summary} />
      {expanded && (
        <div className="mt-1 ml-2 border-l-2 border-border pl-2">
          <ObjectTable
            data={data}
            depth={depth + 1}
            maxDepth={maxDepth}
            expandedByDefault={expandedByDefault}
            visited={visited}
          />
        </div>
      )}
    </div>
  );
}

// ─── CollapsibleArrayTable ──────────────────────────────────────────────────

function CollapsibleArrayTable({
  data,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: {
  data: unknown[];
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}) {
  const [expanded, setExpanded] = useState(expandedByDefault);
  const toggle = useCallback(() => setExpanded((v) => !v), []);

  const summary = `[${data.length}]`;

  return (
    <div className="inline-block">
      <CollapseToggle expanded={expanded} onClick={toggle} summary={summary} />
      {expanded && (
        <div className="mt-1 ml-2 border-l-2 border-border pl-2">
          {isArrayOfObjects(data) ? (
            <ArrayTable
              data={data as Record<string, unknown>[]}
              depth={depth + 1}
              maxDepth={maxDepth}
              expandedByDefault={expandedByDefault}
              visited={visited}
            />
          ) : (
            <MixedArrayTable
              data={data}
              depth={depth + 1}
              maxDepth={maxDepth}
              expandedByDefault={expandedByDefault}
              visited={visited}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── ObjectTable (key-value pairs) ──────────────────────────────────────────

function ObjectTable({
  data,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: {
  data: Record<string, unknown>;
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}) {
  const keys = useMemo(() => Object.keys(data), [data]);

  return (
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr>
          <th className={cn(
            'px-3 py-1.5 text-left font-medium text-muted-foreground',
            'bg-muted/50 border-b border-border sticky top-0',
          )}>
            Property
          </th>
          <th className={cn(
            'px-3 py-1.5 text-left font-medium text-muted-foreground',
            'bg-muted/50 border-b border-border sticky top-0',
          )}>
            Value
          </th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key, idx) => (
          <tr
            key={key}
            className={cn(idx % 2 === 1 && 'bg-muted/10')}
          >
            <td className="px-3 py-1.5 border-b border-border align-top font-medium text-muted-foreground whitespace-nowrap">
              {key}
            </td>
            <td className="px-3 py-1.5 border-b border-border align-top">
              <ValueCell
                value={data[key]}
                depth={depth}
                maxDepth={maxDepth}
                expandedByDefault={expandedByDefault}
                visited={visited}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── ArrayTable (array of objects with column headers) ──────────────────────

function ArrayTable({
  data,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: {
  data: Record<string, unknown>[];
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const columns = useMemo(() => extractColumns(data), [data]);
  const visibleRows = useMemo(() => data.slice(0, visibleCount), [data, visibleCount]);
  const remaining = data.length - visibleCount;

  const showMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, data.length));
  }, [data.length]);

  return (
    <div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className={cn(
                  'px-3 py-1.5 text-left font-medium text-muted-foreground',
                  'bg-muted/50 border-b border-border sticky top-0',
                )}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className={cn(rowIdx % 2 === 1 && 'bg-muted/10')}
            >
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-3 py-1.5 border-b border-border align-top"
                >
                  <ValueCell
                    value={row != null && typeof row === 'object' ? (row as Record<string, unknown>)[col] : undefined}
                    depth={depth}
                    maxDepth={maxDepth}
                    expandedByDefault={expandedByDefault}
                    visited={visited}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {remaining > 0 && (
        <button
          type="button"
          onClick={showMore}
          className={cn(
            'mt-2 px-3 py-1 text-xs text-muted-foreground',
            'hover:text-foreground border border-border rounded',
            'hover:bg-muted/50 transition-colors cursor-pointer',
          )}
        >
          Show more ({remaining})
        </button>
      )}
    </div>
  );
}

// ─── MixedArrayTable (array with non-object items) ──────────────────────────

function MixedArrayTable({
  data,
  depth,
  maxDepth,
  expandedByDefault,
  visited,
}: {
  data: unknown[];
  depth: number;
  maxDepth: number;
  expandedByDefault: boolean;
  visited: WeakSet<object>;
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visibleRows = useMemo(() => data.slice(0, visibleCount), [data, visibleCount]);
  const remaining = data.length - visibleCount;

  const showMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, data.length));
  }, [data.length]);

  return (
    <div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className={cn(
              'px-3 py-1.5 text-left font-medium text-muted-foreground',
              'bg-muted/50 border-b border-border sticky top-0 w-16',
            )}>
              Index
            </th>
            <th className={cn(
              'px-3 py-1.5 text-left font-medium text-muted-foreground',
              'bg-muted/50 border-b border-border sticky top-0',
            )}>
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((item, idx) => (
            <tr
              key={idx}
              className={cn(idx % 2 === 1 && 'bg-muted/10')}
            >
              <td className="px-3 py-1.5 border-b border-border align-top text-muted-foreground font-mono">
                {idx}
              </td>
              <td className="px-3 py-1.5 border-b border-border align-top">
                <ValueCell
                  value={item}
                  depth={depth}
                  maxDepth={maxDepth}
                  expandedByDefault={expandedByDefault}
                  visited={visited}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {remaining > 0 && (
        <button
          type="button"
          onClick={showMore}
          className={cn(
            'mt-2 px-3 py-1 text-xs text-muted-foreground',
            'hover:text-foreground border border-border rounded',
            'hover:bg-muted/50 transition-colors cursor-pointer',
          )}
        >
          Show more ({remaining})
        </button>
      )}
    </div>
  );
}

// ─── PrimitiveDisplay (top-level primitive) ─────────────────────────────────

function PrimitiveDisplay({ value }: { value: unknown }) {
  return (
    <div className="p-4">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className={cn(
              'px-3 py-1.5 text-left font-medium text-muted-foreground',
              'bg-muted/50 border-b border-border',
            )}>
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-3 py-1.5 border-b border-border">
              <span className="font-mono">{formatPrimitive(value)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

function StructuredTableViewInner({
  data,
  maxDepth = 3,
  expandedByDefault = false,
}: StructuredTableViewProps) {
  const parsed = useMemo<{ data: unknown } | { error: string }>(() => {
    try {
      return { data: parseInput(data) };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Unable to parse input' };
    }
  }, [data]);

  if ('error' in parsed) {
    return (
      <div className="p-4 text-red-500">
        <div className="font-semibold">Parse Error</div>
        <div className="text-sm mt-1">{parsed.error}</div>
        <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-32">
          {data.substring(0, 200)}...
        </pre>
      </div>
    );
  }

  const value = parsed.data;
  const visited = useMemo(() => new WeakSet<object>(), []);

  // Top-level primitive
  if (isPrimitive(value)) {
    return <PrimitiveDisplay value={value} />;
  }

  // Top-level array of objects → table with column headers
  if (Array.isArray(value) && isArrayOfObjects(value)) {
    return (
      <div className="p-4 overflow-auto">
        <ArrayTable
          data={value as Record<string, unknown>[]}
          depth={1}
          maxDepth={maxDepth}
          expandedByDefault={expandedByDefault}
          visited={visited}
        />
      </div>
    );
  }

  // Top-level mixed array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="p-4 text-muted-foreground italic text-sm">
          Empty array
        </div>
      );
    }
    return (
      <div className="p-4 overflow-auto">
        <MixedArrayTable
          data={value}
          depth={1}
          maxDepth={maxDepth}
          expandedByDefault={expandedByDefault}
          visited={visited}
        />
      </div>
    );
  }

  // Top-level object → key-value table
  if (typeof value === 'object' && value !== null) {
    if (Object.keys(value).length === 0) {
      return (
        <div className="p-4 text-muted-foreground italic text-sm">
          Empty object
        </div>
      );
    }
    return (
      <div className="p-4 overflow-auto">
        <ObjectTable
          data={value as Record<string, unknown>}
          depth={1}
          maxDepth={maxDepth}
          expandedByDefault={expandedByDefault}
          visited={visited}
        />
      </div>
    );
  }

  return <PrimitiveDisplay value={value} />;
}

export function StructuredTableView(props: StructuredTableViewProps) {
  return (
    <ErrorBoundary>
      <StructuredTableViewInner {...props} />
    </ErrorBoundary>
  );
}
